import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, GraduationCap, Heart, Rocket } from 'lucide-react';
type Role = 'learner' | 'parent' | 'teacher';
const ROLE_CONFIG: Record<Role, {
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
  activeBg: string;
  activeBorder: string;
  activeText: string;
}> = {
  learner: {
    icon: <Rocket className="w-6 h-6" data-id="element-605" />,
    label: 'Leerling',
    description: 'Ik wil oefenen en leren!',
    color: 'text-orange-500',
    activeBg: 'bg-orange-50',
    activeBorder: 'border-orange-400',
    activeText: 'text-orange-700'
  },
  parent: {
    icon: <Heart className="w-6 h-6" data-id="element-606" />,
    label: 'Ouder',
    description: 'Ik volg de voortgang van mijn kind',
    color: 'text-emerald-500',
    activeBg: 'bg-emerald-50',
    activeBorder: 'border-emerald-400',
    activeText: 'text-emerald-700'
  },
  teacher: {
    icon: <GraduationCap className="w-6 h-6" data-id="element-607" />,
    label: 'Docent',
    description: 'Ik beheer mijn klas',
    color: 'text-indigo-500',
    activeBg: 'bg-indigo-50',
    activeBorder: 'border-indigo-400',
    activeText: 'text-indigo-700'
  }
};
interface RegisterRoleStepProps {
  role: Role;
  onSelectRole: (role: Role) => void;
  slideVariants: Record<string, unknown>;
}
export function RegisterRoleStep({
  role,
  onSelectRole,
  slideVariants
}: RegisterRoleStepProps) {
  return <motion.div key="step-role" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3
  }} className="space-y-6" data-id="element-608">
      <h2 className="text-xl font-black text-slate-900 text-center mb-2" data-id="element-609">
        Wie ben jij?
      </h2>

      {/* Learner Hero Card */}
      <motion.button type="button" onClick={() => onSelectRole('learner')} className={`relative w-full p-6 rounded-3xl border-2 text-left font-bold transition-all overflow-hidden ${role === 'learner' ? 'border-orange-400 shadow-md scale-[1.02]' : 'border-orange-200 shadow-sm hover:border-orange-300 hover:shadow-md'}`} whileTap={{
      scale: 0.98
    }} data-id="element-610">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-100/50 -z-10" data-id="element-611" />
        <div className="absolute top-0 right-0 bg-orange-500 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-bl-xl rounded-tr-xl" data-id="element-612">
          Meest gekozen!
        </div>

        <div className="flex items-center gap-5" data-id="element-613">
          <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-614">
            <Rocket className="w-8 h-8" data-id="element-615" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-616">
            <div className="text-2xl text-orange-900 mb-1" data-id="element-617">Leerling</div>
            <div className="text-sm font-medium text-orange-700/80" data-id="element-618">
              Ik wil oefenen en leren!
            </div>
          </div>
          <ChevronRight className={`w-6 h-6 flex-shrink-0 transition-transform ${role === 'learner' ? 'text-orange-600 translate-x-1' : 'text-orange-400'}`} data-id="element-619" />
        </div>
      </motion.button>

      <div className="relative flex items-center py-2" data-id="element-620">
        <div className="flex-grow border-t border-slate-100" data-id="element-621"></div>
        <span className="flex-shrink-0 mx-4 text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-622">
          Of ben je een
        </span>
        <div className="flex-grow border-t border-slate-100" data-id="element-623"></div>
      </div>

      {/* Parent & Teacher Row */}
      <div className="grid grid-cols-2 gap-3" data-id="element-624">
        {(['parent', 'teacher'] as Role[]).map(r => {
        const cfg = ROLE_CONFIG[r];
        const isActive = role === r;
        return <motion.button key={r} type="button" onClick={() => onSelectRole(r)} className={`p-4 rounded-2xl border-2 text-center font-bold transition-all flex flex-col items-center gap-3 ${isActive ? `${cfg.activeBorder} ${cfg.activeBg} ${cfg.activeText} shadow-sm` : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`} whileTap={{
          scale: 0.96
        }} data-id="element-625">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isActive ? 'bg-white shadow-sm' : 'bg-slate-100'} ${isActive ? cfg.activeText : cfg.color}`} data-id="element-626">
                {cfg.icon}
              </div>
              <div className="text-sm" data-id="element-627">{cfg.label}</div>
            </motion.button>;
      })}
      </div>
    </motion.div>;
}